import React, { useState, useMemo } from 'react';
import { Stack, Text } from '@fluentui/react';
import FeatureCard, { Feature } from './FeatureCard';
import FeatureSearch from './FeatureSearch';
import styles from './FeatureGrid.module.css';

interface Category {
  title: { en: string; he: string };
  description: { en: string; he: string };
  icon: string;
}

interface QuickStartStep {
  order: number;
  title: { en: string; he: string };
  description: { en: string; he: string };
  icon: string;
}

interface HelpFeaturesData {
  features: Feature[];
  categories: Record<string, Category>;
  quickStartSteps: QuickStartStep[];
  metadata: {
    version: string;
    lastUpdated: string;
    languages: string[];
  };
}

interface FeatureGridProps {
  data: HelpFeaturesData;
}

type FilterCategory = 'all' | 'source' | 'feature';

const FeatureGrid: React.FC<FeatureGridProps> = ({ data }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<FilterCategory>('all');
  const [expandedFeatureId, setExpandedFeatureId] = useState<string | null>(null);

  const handleToggleExpand = (id: string) => {
    setExpandedFeatureId(expandedFeatureId === id ? null : id);
  };

  const filteredFeatures = useMemo(() => {
    let features = data.features;

    // Filter by category
    if (activeCategory !== 'all') {
      features = features.filter(f => f.category === activeCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      features = features.filter(f => {
        // Search in title
        if (f.title?.en?.toLowerCase().includes(query) || f.title?.he?.includes(query)) return true;
        // Search in description
        if (f.description?.en?.toLowerCase().includes(query) || f.description?.he?.includes(query)) return true;
        // Search in trigger words
        if (f.triggerWords?.en?.some(w => w.toLowerCase().includes(query))) return true;
        if (f.triggerWords?.he?.some(w => w.includes(query))) return true;
        // Search in examples
        if (f.examples?.some(e => 
          e.query?.en?.toLowerCase().includes(query) || 
          e.query?.he?.includes(query) ||
          e.explanation?.en?.toLowerCase().includes(query)
        )) return true;
        return false;
      });
    }

    // Sort by popularity
    return features.sort((a, b) => b.popularity - a.popularity);
  }, [data.features, activeCategory, searchQuery]);

  const sourceFeatures = filteredFeatures.filter(f => f.category === 'source');
  const otherFeatures = filteredFeatures.filter(f => f.category === 'feature');

  return (
    <div className={styles.container}>
      {/* Search and Filter */}
      <FeatureSearch
        value={searchQuery}
        onChange={setSearchQuery}
        activeCategory={activeCategory}
        onCategoryChange={setActiveCategory}
      />

      {/* Quick Start Section (no search active) */}
      {!searchQuery && (
        <div className={styles.quickStart}>
          <div className={styles.quickStartHeader}>
            <Text className={styles.quickStartTitle}>👋 Quick Start Guide</Text>
            <Text className={styles.quickStartSubtitle}>
              This app routes queries to specific data sources based on keywords
            </Text>
          </div>
          <div className={styles.quickStartSteps}>
            {data.quickStartSteps.map((step) => (
              <div key={step.order} className={styles.quickStartStep}>
                <span className={styles.stepIcon}>{step.icon}</span>
                <div className={styles.stepContent}>
                  <Text className={styles.stepTitle}>{step.title.en}</Text>
                  <Text className={styles.stepDescription}>{step.description.en}</Text>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Results count when searching */}
      {searchQuery && (
        <div className={styles.searchResults}>
          <Text className={styles.resultsCount}>
            Found {filteredFeatures.length} result{filteredFeatures.length !== 1 ? 's' : ''} for "{searchQuery}"
          </Text>
        </div>
      )}

      {/* Search Sources Section */}
      {sourceFeatures.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionIcon}>🔍</span>
            <Text className={styles.sectionTitle}>Search Sources</Text>
            <Text className={styles.sectionDescription}>
              Where the app searches for information
            </Text>
          </div>
          <div className={styles.grid}>
            {sourceFeatures.map(feature => (
              <FeatureCard
                key={feature.id}
                feature={feature}
                isExpanded={expandedFeatureId === feature.id}
                onToggleExpand={handleToggleExpand}
                searchHighlight={searchQuery}
              />
            ))}
          </div>
        </div>
      )}

      {/* Features Section */}
      {otherFeatures.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionIcon}>⚙️</span>
            <Text className={styles.sectionTitle}>Features & Tools</Text>
            <Text className={styles.sectionDescription}>
              App capabilities and how to use them
            </Text>
          </div>
          <div className={styles.grid}>
            {otherFeatures.map(feature => (
              <FeatureCard
                key={feature.id}
                feature={feature}
                isExpanded={expandedFeatureId === feature.id}
                onToggleExpand={handleToggleExpand}
                searchHighlight={searchQuery}
              />
            ))}
          </div>
        </div>
      )}

      {/* No results */}
      {filteredFeatures.length === 0 && (
        <div className={styles.noResults}>
          <span className={styles.noResultsIcon}>🔍</span>
          <Text className={styles.noResultsTitle}>No features found</Text>
          <Text className={styles.noResultsDescription}>
            Try adjusting your search or filters
          </Text>
        </div>
      )}
    </div>
  );
};

export default FeatureGrid;
